import React from 'react'
import {Alert, Card, Collapse, Divider, Space, Tag, Typography} from 'antd'

const {Title, Paragraph, Text, Link} = Typography
const {Panel} = Collapse

// 分发根地址与 lechun-cli 的 install.sh 默认值、服务端 cli.auth.download.* 保持一致，
// 统一走 OSS/CDN（docs/14 方案 B）。不能指向 Portal 自身：
// 未上传产物时 Portal 是 SPA 兜底，会返回 200 + HTML，被 `curl -fsSL ... | bash` 当成脚本执行。
export const CLI_DOWNLOAD_BASE = 'https://resource.lechun.cc/lechun-cli'
export const CLI_INSTALL_COMMAND_MAC = `curl -fsSL ${CLI_DOWNLOAD_BASE}/install.sh | bash`
export const CLI_INSTALL_COMMAND_WINDOWS = `irm ${CLI_DOWNLOAD_BASE}/install.ps1 | iex`
export const CLI_MIN_VERSION_FOR_STABLE_LOGIN = '1.4.5'

export const CLI_DOWNLOADS = [
    {label: 'macOS (Apple Silicon)', file: 'latest/lechun-cli-darwin-arm64'},
    {label: 'macOS (Intel)', file: 'latest/lechun-cli-darwin-amd64'},
    {label: 'Windows (x64)', file: 'latest/lechun-cli-windows-amd64.exe'},
]

// 给同事复制到 DSH / Codex 里，让 AI 代劳安装、登录与首次自检。
export const CLI_AI_INSTALL_PROMPT = `请帮我安装并登录公司统一的命令行工具 lechun-cli。

1. 执行安装命令（macOS）：${CLI_INSTALL_COMMAND_MAC}
   Windows 用 PowerShell：${CLI_INSTALL_COMMAND_WINDOWS}
   如果命令找不到，把 ~/.local/bin 加进 PATH 后重开终端。
2. 执行 lechun-cli auth login，我会在浏览器里点授权；请等终端打印「登录成功」再继续。
3. 依次执行 lechun-cli version、lechun-cli auth status、lechun-cli context，把结果摘要给我。
4. 之后先用只读命令了解能力：lechun-cli guide overview。

注意：全程不要打印、复制或提交任何 token / 密码 / AccessKey；不要执行上传、删除等写操作。`

const COMMON_COMMANDS = [
    {command: 'lechun-cli context', desc: '看公司有哪些能力、该用哪个工具'},
    {command: 'lechun-cli guide overview', desc: '能力总览与详细说明'},
    {command: 'lechun-cli auth status', desc: '查看登录状态'},
    {command: 'lechun-cli vibe project list', desc: '查看我的 Vibe 项目'},
    {command: 'lechun-cli db accounts', desc: '查看可用的数据库账号'},
    {command: 'lechun-cli aic self-check', desc: 'AIC 数据资产自检'},
    {command: 'lechun-cli doctor', desc: '本机环境自检（装完排查问题先跑它）'},
    {command: 'lechun-cli update', desc: '手动升级到最新版本'},
]

function CodeLine({command}) {
    return (
        <Paragraph style={{marginBottom: 8}}>
            <Text code copyable={{text: command}}>{command}</Text>
        </Paragraph>
    )
}

export function CliInstallContent() {
    return (
        <div className="cli-install-content">
            <Paragraph type="secondary">
                乐纯统一命令行工具（lechun-cli）：一次安装，之后自动更新。用来登录公司系统、管理 Vibe 项目与 AIC 数据资产、
                上传文件/静态页面、查询数据库，也是 AI 助手调用公司能力的入口。
            </Paragraph>

            <Alert
                type="info"
                showIcon
                style={{marginBottom: 16}}
                message="装完先登录，再执行 lechun-cli context"
                description="context 会列出公司当前有哪些工具、适用什么场景；AI 助手每次操作前也会先读它。"
            />

            <Card title="macOS 安装" style={{marginBottom: 16}}>
                <Paragraph>打开「终端」，执行：</Paragraph>
                <CodeLine command={CLI_INSTALL_COMMAND_MAC} />
                <Paragraph type="secondary" style={{marginBottom: 0}}>
                    安装脚本会校验 sha256，并把可执行文件放到 <Text code>~/.local/bin</Text>。
                </Paragraph>
            </Card>

            <Card title="Windows 安装" style={{marginBottom: 16}}>
                <Paragraph>打开 PowerShell，执行：</Paragraph>
                <CodeLine command={CLI_INSTALL_COMMAND_WINDOWS} />
                <Paragraph type="secondary" style={{marginBottom: 0}}>
                    安装后需要重新打开终端，PATH 才会生效。
                </Paragraph>
            </Card>

            <Card title="安装后：登录" style={{marginBottom: 16}}>
                <Paragraph>
                    <CodeLine command="lechun-cli auth login" />
                </Paragraph>
                <Paragraph>
                    浏览器会打开授权页，确认后<Text strong>回到终端，看到「登录成功」再继续</Text>；
                    授权等待窗口为 10 分钟，超时重跑一次即可。
                </Paragraph>
                <Space wrap>
                    <Tag color="blue">lechun-cli version</Tag>
                    <Tag color="blue">lechun-cli auth status</Tag>
                    <Tag color="blue">lechun-cli context</Tag>
                </Space>
            </Card>

            <Card title="常用命令" style={{marginBottom: 16}}>
                <Space direction="vertical" size={2} style={{width: '100%'}}>
                    {COMMON_COMMANDS.map((item) => (
                        <div key={item.command} style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                            <Text code copyable={{text: item.command}} style={{minWidth: 260, display: 'inline-block'}}>
                                {item.command}
                            </Text>
                            <Text type="secondary">{item.desc}</Text>
                        </div>
                    ))}
                </Space>
            </Card>

            <Card title="让 AI 助手帮你装" style={{marginBottom: 16}}>
                <Paragraph type="secondary">
                    把下面这段贴进 DSH / Codex，它会替你装好、登录并做一次自检（登录时需要你在浏览器点授权）：
                </Paragraph>
                <Paragraph>
                    <Text copyable={{text: CLI_AI_INSTALL_PROMPT}} type="secondary">
                        （点击右侧复制按钮复制完整提示词）
                    </Text>
                </Paragraph>
                <Collapse ghost>
                    <Panel header="展开提示词全文" key="prompt">
                        <pre style={{
                            whiteSpace: 'pre-wrap',
                            background: 'rgba(0,0,0,0.03)',
                            padding: 12,
                            borderRadius: 6,
                            fontSize: 12,
                            margin: 0,
                        }}>
                            {CLI_AI_INSTALL_PROMPT}
                        </pre>
                    </Panel>
                </Collapse>
            </Card>

            <Card title="给 AI 助手的第一条指令" style={{marginBottom: 16}}>
                <Paragraph>
                    安装并登录后，让 AI 先读取能力目录，再决定用哪个工具、把数据放到哪个资产：
                </Paragraph>
                <CodeLine command="lechun-cli context --json" />
                <Paragraph type="secondary" style={{marginBottom: 0}}>
                    需要「需求 → 工具 → 资产」决策树时执行{' '}
                    <Text code copyable={{text: 'lechun-cli guide data-onboarding'}}>
                        lechun-cli guide data-onboarding
                    </Text>
                    ；线下数据一律走 AIC，不要传到 Vibe。
                </Paragraph>
            </Card>

            <Card title="手动下载" style={{marginBottom: 16}}>
                <Space direction="vertical">
                    {CLI_DOWNLOADS.map((item) => (
                        <Link key={item.file} href={`${CLI_DOWNLOAD_BASE}/${item.file}`} target="_blank">
                            {item.label}
                        </Link>
                    ))}
                    <Link href={`${CLI_DOWNLOAD_BASE}/latest/checksums.txt`} target="_blank">
                        checksums.txt（校验用）
                    </Link>
                </Space>
            </Card>

            <Card title="常见问题" style={{marginBottom: 16}}>
                <Collapse ghost>
                    <Panel header="每次都要求重新授权 / 提示「请重新登录」" key="reauth">
                        <Paragraph>
                            先确认版本：<Text code>lechun-cli version</Text>，低于{' '}
                            <Text code>{CLI_MIN_VERSION_FOR_STABLE_LOGIN}</Text> 请先升级：
                            <Text code>lechun-cli update</Text>（或重跑一次安装命令）。
                        </Paragraph>
                        <Paragraph type="secondary" style={{marginBottom: 0}}>
                            1.4.5 起 access token 过期会自动续期、并发刷新不再互相打掉；同时登录授权窗口延长到 10 分钟。
                        </Paragraph>
                    </Panel>
                    <Panel header="浏览器授权成功，但终端没反应" key="timeout">
                        <Paragraph style={{marginBottom: 0}}>
                            授权窗口是 10 分钟：点到超时的话这次授权不会签发凭证，重新执行{' '}
                            <Text code>lechun-cli auth login</Text> 并留在终端等「登录成功」即可。
                        </Paragraph>
                    </Panel>
                    <Panel header="安装后提示命令找不到" key="path">
                        <Paragraph style={{marginBottom: 0}}>
                            把 <Text code>~/.local/bin</Text> 加入 PATH 后重开终端；Windows 需要重新打开 PowerShell。
                        </Paragraph>
                    </Panel>
                    <Panel header="其它问题怎么排查" key="doctor">
                        <Paragraph style={{marginBottom: 0}}>
                            先跑 <Text code>lechun-cli doctor</Text>，它会输出系统、网络、凭证状态；把输出发给技术中心即可。
                        </Paragraph>
                    </Panel>
                </Collapse>
            </Card>

            <Alert
                type="info"
                showIcon
                message="版本说明"
                description="CLI 启动时会检查服务端要求的最低版本；低于要求时会提示并支持自动升级。遇到升级失败，请重新执行上面的安装命令。"
            />

            <Divider />

            <Paragraph type="secondary" style={{marginBottom: 0}}>
                详细使用说明：<Text code>lechun-cli guide overview</Text>；能力目录也可以直接在 CLI 里用{' '}
                <Text code>lechun-cli context</Text> 查看。遇到问题联系技术中心。
                {' '}安装包下载：<Link href={`${CLI_DOWNLOAD_BASE}/latest/checksums.txt`} target="_blank">校验清单</Link>
            </Paragraph>
        </div>
    )
}

export default function CliInstallTab() {
    return (
        <div className="profile-cli-install-tab" style={{padding: '16px 0'}}>
            <Title level={4} style={{marginTop: 0}}>安装乐纯 CLI</Title>
            <CliInstallContent />
        </div>
    )
}
